/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { throttle } from 'lodash';
import {
  getBurndownData,
  getLeanSeries,
  getWorkDensitySeries,
  getCumulativeFlowSeries,
  fetchGanttData
} from '../../actions/workPackageActions';
import { isKanbanMode } from '../../selectors/teamSelector';
import { onResizeDone } from '../../utils/browserHelpers';
import Burndown from './burndown';
import Lean from './lean';
import WorkDensity from './workDensity';
import CumulativeFlow from './cumulativeFlow';
import BurndownLegend from '../Graph/burndownLegend';
import Gantt from '../Graph/gantt';
import DropDownMenu from '../Common/dropDownMenu';
import VrsDropdown from '../PlanWall/vrsDropdown';

const GRAPH_HEIGHT = 200;

class Metrics extends React.Component {
  constructor() {
    super();

    this.burndownChart = null;
    this.cumulativeChart = null;

    this.burndownDiv = React.createRef();
    this.cumulativeFlowDiv = React.createRef();
    this.ganttDiv = React.createRef();
    this.leanDiv = React.createRef();
    this.idleDiv = React.createRef();
    this.flexRow = React.createRef();

    this.state = {
      showBurndown: false,
      cumulativeFlowWidth: 0,
      burndownWidth: 0,
      leanWidth: 0,
      idleWidth: 0,
      ganttWidth: 0,
      isFetchingGraphs: {
        lean: false,
        burndown: false,
        idle: false,
        cumulative: false,
        gantt: false
      },
      isFull: {
        lean: false,
        burndown: false,
        idle: false,
        cumulative: false,
        gantt: false
      },
      dropDown: {},
      resultType: {
        risk: '',
        size: '',
        value: ''
      },
      // oscillator: false,
      containerHeight: 234,
      resizing: false
    };
    this.changingLeanFilter = false;
  }

  componentDidMount() {
    this.calculateChartHeight();
    window.addEventListener('resize', this.calculateChartHeight);
    onResizeDone(
      true,
      `resizeCharts-${this.props.workPackage.level}`,
      this.handleResize,
      this.stopResizing,
      300
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { isFetchingGraphs, showBurndown } = this.state;
    const { metrics, activeTab } = this.props;
    if (prevProps.activeTab !== 'metrics' && activeTab === 'metrics') {
      this.getMetrics();
    }
    if (isFetchingGraphs.burndown && metrics.burndown) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, burndown: false }
      });
    }
    if (isFetchingGraphs.lean && metrics.lean) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, lean: false }
      });
    }
    if (isFetchingGraphs.idle && metrics.idle) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, idle: false }
      });
    }
    if (isFetchingGraphs.cumulative && metrics.cumulative) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, cumulative: false }
      });
    }

    if (isFetchingGraphs.gantt && metrics.gantt) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, gantt: false }
      });
    }

    const burndownWidth = this.burndownDiv.current
      ? this.burndownDiv.current.offsetWidth
      : 0;

    // Resize charts when showing burndown
    if (showBurndown && !prevState.showBurndown) {
      this.burndownChart.resize({
        width: burndownWidth
      });
    }

    if (burndownWidth !== this.state.burndownWidth) {
      this.setState({ burndownWidth });
    }

    const cumulativeFlowWidth = this.cumulativeFlowDiv.current
      ? this.cumulativeFlowDiv.current.offsetWidth
      : 0;
    if (cumulativeFlowWidth !== this.state.cumulativeFlowWidth) {
      this.setState({ cumulativeFlowWidth });
    }

    const ganttWidth = this.ganttDiv.current
      ? this.ganttDiv.current.offsetWidth
      : 0;

    if (ganttWidth !== this.state.ganttWidth) {
      this.setState({ ganttWidth });
    }

    const leanWidth = this.leanDiv.current
      ? this.leanDiv.current.offsetWidth
      : 0;
    if (leanWidth !== this.state.leanWidth) {
      this.setState({ leanWidth });
    }

    const idleWidth = this.idleDiv.current
      ? this.idleDiv.current.offsetWidth
      : 0;
    if (idleWidth !== this.state.idleWidth) {
      this.setState({ idleWidth });
    }

    if (prevProps.activeTab !== this.props.activeTab) {
      if (this.props.activeTab === 'metrics') this.calculateChartHeight();

      this.setState({
        cumulativeFlowWidth,
        isFull: {
          lean: false,
          burndown: false,
          idle: false,
          cumulative: false
        }
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateChartHeight);
    onResizeDone(false, `resizeCharts-${this.props.workPackage.level}`);
  }

  handleResize = throttle(() => {
    this.setState(
      {
        resizing: true,
        isFull: {
          lean: false,
          burndown: false,
          idle: false,
          cumulative: false,
          gantt: false
        }
      },
      this.calculateChartHeight
    );
  }, 100);

  calculateChartHeight = () => {
    const containerHeight =
      document.querySelector(
        `#workpackage-detail-${this.props.workPackage.id} .flex-row`
      ).offsetHeight || 234;
    this.setState({ containerHeight });
  };

  getMetrics = () => {
    const { workPackage } = this.props;
    const { isFetchingGraphs } = this.state;
    if (workPackage && workPackage.id) {
      if (!isFetchingGraphs.burndown && workPackage.level === 1) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, burndown: true }
        }));
        this.props.getBurndownData(workPackage.id);
      }
      if (!isFetchingGraphs.lean) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, lean: true }
        }));
        this.props.getLeanSeries(workPackage.id);
      }
      if (!isFetchingGraphs.cumulative) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, cumulative: true }
        }));
        this.props.getCumulativeFlowSeries(workPackage.id);
      }
      if (!isFetchingGraphs.idle) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, idle: true }
        }));
        this.props.getWorkDensitySeries(workPackage.id);
      }
      if (!isFetchingGraphs.gantt && workPackage.level > 1) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, gantt: true }
        }));
        this.props.fetchGanttData(workPackage.id);
      }
    }
  };

  setBurndownChart = (chart) => {
    this.burndownChart = chart;
  };

  stopResizing = () => this.setState({ resizing: false });

  resizeGraphsWhenFull = (chart) => {
    const { isFull } = this.state;
    if (chart === 'burndown') {
      const burndownWidth = this.burndownDiv.current
        ? this.burndownDiv.current.offsetWidth
        : 0;
      const multiplicator = isFull.burndown ? 2 : 1;
      this.setState({
        burndownWidth
      });
      this.burndownChart.resize({
        width: burndownWidth * multiplicator
      });
    }

    if (chart === 'gantt') {
      const ganttWidth = this.ganttDiv.current
        ? this.ganttDiv.current.offsetWidth
        : 0;
      const multiplicator = isFull.gantt ? 2 : 1;
      this.setState({ ganttWidth: ganttWidth * multiplicator });
    }

    if (chart === 'cumulative') {
      const cumulativeFlowWidth = this.cumulativeFlowDiv.current
        ? this.cumulativeFlowDiv.current.offsetWidth
        : 0;
      const multiplicator = isFull.cumulative ? 2 : 1;
      this.setState({
        cumulativeFlowWidth: cumulativeFlowWidth * multiplicator
      });
    }

    if (chart === 'lean') {
      const leanWidth = this.leanDiv.current
        ? this.leanDiv.current.offsetWidth
        : 0;
      this.setState({
        leanWidth
      });
    }

    if (chart === 'idle') {
      const idleWidth = this.idleDiv.current
        ? this.idleDiv.current.offsetWidth
        : 0;
      this.setState({
        idleWidth
      });
    }
  };

  toggleIsFull = (chart) => {
    const { isFull } = this.state;
    if (this.changingLeanFilter) {
      this.changingLeanFilter = false;
      return;
    }
    this.setState(
      (prevState) => ({
        isFull: { ...isFull, [chart]: !prevState.isFull[chart] }
      }),
      () => {
        this.resizeGraphsWhenFull(chart);
      }
    );
  };

  generateGanttWidth = () => {
    const { ganttWidth, isFull } = this.state;

    if (ganttWidth < 20) return 0;

    if (isFull.gantt) return ganttWidth - 40;

    return ganttWidth - 20;
  };

  generateCFWidth = () => {
    const { cumulativeFlowWidth } = this.state;

    if (cumulativeFlowWidth < 10) return 0;

    return cumulativeFlowWidth - 30;
  };

  handleResultTypeChange = (type, newValue) => {
    const { workPackage } = this.props;
    const { resultType } = this.state;
    this.changingLeanFilter = true; // hack to suppress the click through to the underlying panel and toggle of full size
    resultType[type] = newValue.type_attributes[type];
    this.setState({ resultType }, () => {
      this.setIsFetching('lean');
      this.props.getLeanSeries(workPackage.id, resultType);
    });
  };

  resetResultType = () => {
    const { workPackage } = this.props;
    this.changingLeanFilter = true;
    const initialResultType = {
      risk: '',
      size: '',
      value: ''
    };

    this.setState({ resultType: initialResultType }, () => {
      this.setIsFetching('lean');
      this.props.getLeanSeries(workPackage.id);
    });
  };

  isResetButtonActive = () => {
    const {
      resultType: { risk, size, value }
    } = this.state;
    return !risk && !size && !value;
  };

  setIsFetching = (name) => {
    this.setState((prevState) => ({
      isFetchingGraphs: { ...prevState.isFetchingGraphs, [name]: true }
    }));
  };

  toggleDropDownMenu = (e, type, props) => {
    e.stopPropagation();
    let { dropDown } = this.state;
    if (dropDown.id) {
      dropDown = {};
    } else {
      const newProps = {
        ...props
      };
      const { target: element } = e;
      dropDown = {
        ...newProps,
        type,
        elementClicked: element,
        elementAttached: element.closest('.dropdown'),
        parent: element.parentNode,
        id: type,
        closeDropdown: () => this.setState({ dropDown: {} })
      };
    }
    this.setState({ dropDown });
  };

  render() {
    const {
      metrics,
      workPackage,
      tasks,
      isKanban,
      cadences,
      children = [],
      defaultOptions
    } = this.props;
    const {
      isFetchingGraphs,
      showBurndown,
      burndownWidth,
      leanWidth,
      idleWidth,
      isFull,
      containerHeight,
      resizing,
      dropDown
    } = this.state;
    const isLoading = Object.values(isFetchingGraphs).some(
      (fetching) => fetching
    );

    const displayBurndownGraph = !isKanban || showBurndown;

    let childrenPoints = 0;
    if (children.length > 0) {
      childrenPoints = children.reduce((sum, child) => {
        let newValue = sum;
        if (child.result_type && child.result_type.type_attributes) {
          newValue += child.result_type.type_attributes.size || 0;
        }
        return newValue;
      }, 0);
    }

    if (!metrics) {
      return null;
    }
    return (
      <div
        className="metrics-rows-container"
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        {isLoading && <div id="loader" />}
        {!isLoading && (
          <>
            {(isFull.burndown ||
              isFull.cumulative ||
              isFull.gantt ||
              Object.values(isFull).indexOf(true) === -1) && (
              <div
                className={`row flex-row ${
                  workPackage.level > 1 ? 'higher-level' : ''
                }`}
                ref={this.flexRow}
                style={{ flex: workPackage.level > 1 ? '2' : '4' }}
              >
                {workPackage.level === 1 &&
                  displayBurndownGraph &&
                  !resizing &&
                  (isFull.burndown ||
                    Object.values(isFull).indexOf(true) === -1) && (
                    <div
                      className={`col-sm-${
                        isFull.burndown ? '12' : '6'
                      } graph-container`}
                      ref={this.burndownDiv}
                      style={{ position: 'relative' }}
                      onClick={() => this.toggleIsFull('burndown')}
                    >
                      <Burndown
                        setBurndownChart={this.setBurndownChart}
                        graphData={metrics.burndown}
                        width={burndownWidth}
                        height={
                          containerHeight - 50 > 0 ? containerHeight - 50 : 0
                        }
                        cadences={cadences}
                        childrenPoints={childrenPoints}
                        workPackageId={workPackage.id}
                      />
                      <BurndownLegend />
                    </div>
                  )}
                {!resizing &&
                  workPackage.level > 1 &&
                  (isFull.gantt ||
                    Object.values(isFull).indexOf(true) === -1) && (
                    <div
                      className={`col-sm-${
                        isFull.gantt ? '12' : '6'
                      } graph-container gantt-container`}
                      ref={this.ganttDiv}
                      style={{ position: 'relative' }}
                      onClick={() => this.toggleIsFull('gantt')}
                    >
                      <Gantt
                        width={this.generateGanttWidth()}
                        height={
                          containerHeight - 125 > 0 ? containerHeight - 125 : 0
                        }
                        workPackage={workPackage}
                        data={metrics.gantt}
                      />
                    </div>
                  )}
                {(isFull.cumulative ||
                  Object.values(isFull).indexOf(true) === -1) &&
                  !resizing && (
                    <div
                      className={`col-sm-${
                        (!displayBurndownGraph && workPackage.level === 1) ||
                        isFull.cumulative
                          ? '12'
                          : '6'
                      } graph-container result-cms`}
                      ref={this.cumulativeFlowDiv}
                      onClick={() => this.toggleIsFull('cumulative')}
                    >
                      {!resizing && (
                        <CumulativeFlow
                          graphData={metrics.cumulative}
                          workPackage={workPackage}
                          width={this.generateCFWidth()}
                          height={
                            containerHeight - 85 > 0 ? containerHeight - 85 : 0
                          }
                        />
                      )}
                    </div>
                  )}
              </div>
            )}
            {(isFull.lean ||
              isFull.idle ||
              Object.values(isFull).indexOf(true) === -1) && (
              <div
                className='flex-row with-children-lead'
                style={{}}
              >
                {(isFull.idle || Object.values(isFull).indexOf(true) === -1) &&
                  !resizing && (
                    <div
                      ref={this.idleDiv}
                      className={`col-sm-${
                        isFull.idle ? '12' : '6'
                      } wp-idle-container`}
                      onClick={() => this.toggleIsFull('idle')}
                    >
                      <WorkDensity
                        graphData={metrics.idle}
                        workPackage={workPackage}
                        tasks={tasks}
                        width={idleWidth - 20 > 0 ? idleWidth - 20 : 0}
                        height={150}
                      />
                    </div>
                  )}
                {(isFull.lean || Object.values(isFull).indexOf(true) === -1) &&
                  !resizing && (
                    <div
                      className={`col-sm-${
                        isFull.lean ? '12' : '6'
                      } wp-lean-container ${workPackage.level > 1 ? 'higher-level' : ''}`}
                      ref={this.leanDiv}
                      onClick={() => this.toggleIsFull('lean')}
                    >
                      <Lean
                        graphData={metrics.lean}
                        workPackage={workPackage}
                        width={leanWidth > 0 ? leanWidth - 100 : leanWidth}
                        height={workPackage.level > 1 ? 120 : 150}
                        defaultOptions={defaultOptions}
                        resultType={workPackage.result_type}
                      />
                      {workPackage.level > 1 ? ([
                      <div className="wp-child-lean-metrics-vrs vrs-dropdown-container">
                        <div className="inner-vrs-container">
                          <button
                            className="vrs-all-button"
                            onClick={this.resetResultType}
                            disabled={this.isResetButtonActive()}
                            type="button"
                          >
                            ALL
                          </button>
                          <VrsDropdown
                            type="value"
                            data={this.state.resultType}
                            level={1}
                            options={
                              defaultOptions && defaultOptions.value_defaults
                                ? defaultOptions.value_defaults.values
                                : {}
                            }
                            handleSelect={(newValue) =>
                              this.handleResultTypeChange('value', newValue)
                            }
                            toggleDropDownMenu={this.toggleDropDownMenu}
                          />
                          <VrsDropdown
                            type="risk"
                            data={this.state.resultType}
                            level={1}
                            options={
                              defaultOptions && defaultOptions.risk_defaults
                                ? defaultOptions.risk_defaults.values
                                : {}
                            }
                            handleSelect={(newValue) =>
                              this.handleResultTypeChange('risk', newValue)
                            }
                            toggleDropDownMenu={this.toggleDropDownMenu}
                          />
                          <VrsDropdown
                            type="size"
                            data={this.state.resultType}
                            level={1}
                            options={
                              defaultOptions && defaultOptions.size_defaults
                                ? defaultOptions.size_defaults.values
                                : {}
                            }
                            handleSelect={(newValue) =>
                              this.handleResultTypeChange('size', newValue)
                            }
                            toggleDropDownMenu={this.toggleDropDownMenu}
                          />
                        </div>
                        {dropDown && dropDown.id && (
                          <DropDownMenu
                            type={dropDown.type}
                            dropDown={dropDown}
                            key={dropDown.id}
                          />
                        )}
                      </div>,                        
                        <Lean
                          graphData={metrics.lean}
                          workPackage={workPackage}
                          childData
                          width={leanWidth > 0 ? leanWidth - 100 : leanWidth}
                          height={120}
                          defaultOptions={defaultOptions}
                          resultType={workPackage.result_type}
                        />
                      ]) : null}
                    </div>
                  )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

Metrics.propTypes = {
  metrics: PropTypes.object
};

const mapStateToProps = (state, ownProps) => ({
  isKanban: isKanbanMode(state),
  // should be in a selector function
  children:
    ownProps.workPackage &&
    ownProps.workPackage.id &&
    ownProps.workPackage.level > 1
      ? state.workPackageReducer.stories.filter(
          (story) => story.parent_id === ownProps.workPackage.id
        )
      : [],
  metrics:
    ownProps.workPackage &&
    ownProps.workPackage.id &&
    state.workPackageReducer.metrics[ownProps.workPackage.id]
      ? state.workPackageReducer.metrics[ownProps.workPackage.id]
      : {},
  defaultOptions: state.defaultOptionsReducer.defaultOptions
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getBurndownData,
      getLeanSeries,
      getWorkDensitySeries,
      getCumulativeFlowSeries,
      fetchGanttData
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Metrics);
